/* eslint-disable jsx-a11y/click-events-have-key-events */
import { memo, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isNil, isEmpty, noop } from 'lodash';
import classNames from 'classnames';
// Import UI Components
import { BusyLoader, Divider, Icon, Tooltip } from '@geneui/components';
// Import Components
import PlayersDataTable from 'components/common/PlayersDataTable';
import DataTable from 'components/common/DataTable';
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import DownloadToolbar from 'components/common/DownloadToolbar';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { CommunicationHttpService } from 'services/http';
import { Helpers } from 'services';
// Import Constants
import { l, DownloadExtensions } from 'constants/common';
import { PlayersColumns, TrackedLinksColumnsConfigs } from './config';
// Import Styles
import styles from './promotionTrackedLinksContent.module.scss';
import playersDataTableStyles from 'components/common/PlayersDataTable/playersDataTable.module.scss';

const { saveToDisk } = Helpers;

const PromotionTrackedLinksContent = (props) => {
    const {
        promotionId,
        templateId,
        campaignType,
        showOnlyUnsubscribeLinks,
        templateName,
        linksTableTitle,
        setSelectedTrackedLinkIds,
        className,
    } = props;
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();

    const [trackedLinks, setTrackedLinks] = useState<Nullable<AnyObject[]>>(null);
    const [isLinksLoading, setIsLinksLoading] = useState(true);
    const [isDownloadLinksLoading, setIsDownloadLinksLoading] = useState(false);

    const [players, setPlayers] = useState([]);
    const [isPlayersLoading, setIsPlayersLoading] = useState(false);
    const [isDownloadPlayersLoading, setIsDownloadPlayersLoading] = useState(false);

    const [multiSelectedRows, setMultiSelectedRows] = useState<AnyObject[]>([]);

    const { columns, multiSelectColumnConfig } = TrackedLinksColumnsConfigs(t);

    const { getTrackLinkReportByIds, getTrackedLinks } = useCancelableRequests(
        {
            getTrackLinkReportByIds: CommunicationHttpService.getTrackLinkReportByIds,
            getTrackedLinks: CommunicationHttpService.getTrackedLinks,
        },
        { takeLatest: ['getTrackLinkReportByIds'] },
    );
    // kept out of useCancelableRequests: a file download must not be aborted when the view closes
    const { downloadTrackLinkReport } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- download must survive unmount
            downloadTrackLinkReport: CommunicationHttpService.downloadTrackLinkReport(),
        }),
        [],
    );

    const templateNameOrId = templateName ?? templateId;

    const downloadTrackedLinks = () => {
        setIsDownloadLinksLoading(true);

        const requestBody = {
            TrackLinkIdes: multiSelectedRows.map((row) => row.data?.TrackLinkId),
            CampaignType: campaignType,
            PromotionId: promotionId,
            TemplateId: templateId,
        };

        doPostRequest(downloadTrackLinkReport.request, {
            requestBody,
        }).then((res) => {
            saveToDisk(res, DownloadExtensions.xlsx, templateNameOrId);
            setIsDownloadLinksLoading(false);
        });
    };

    const downloadPlayersData = (type, downloadDropdownValue) => {
        setIsDownloadPlayersLoading(true);

        const trackLinkIds = multiSelectedRows.map((row) => row.data?.TrackLinkId);

        const requestBody = {
            TrackLinkIdes: trackLinkIds,
            TopCount: downloadDropdownValue,
            CampaignType: campaignType,
            PromotionId: promotionId,
            TemplateId: templateId,
        };

        return doPostRequest(downloadTrackLinkReport.request, {
            requestBody,
        }).then((res) => {
            saveToDisk(res, type, `${templateNameOrId}`);
            setIsDownloadPlayersLoading(false);
        });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sendRequest = (request: Nullable<(...any: any[]) => Promise<any>> = null, trackedLinkIds) => {
        if (!isNil(request)) {
            setPlayers([]);
            setIsPlayersLoading(true);
            const requestBody = {
                PromotionId: promotionId,
                CampaignType: campaignType,
                TemplateId: templateId,
                TrackLinkIds: trackedLinkIds,
            };
            doPostRequest(request, {
                requestBody,
                successCallback: (data) => {
                    setPlayers(data);
                },
            }).then(({ IsCanceled }) => {
                // A superseded call must not hide the loader of the call that replaced it
                if (!IsCanceled) {
                    setIsPlayersLoading(false);
                }
            });
        }
    };

    const trackedLinksData = useMemo(() => {
        if (!trackedLinks) return null;

        return trackedLinks
            .filter(({ Url }) => !showOnlyUnsubscribeLinks || Url === 'UnsubscribeLink')
            .map(({ Url, ClickedCount, TrackLinkId }) => ({
                actionBar: false,
                dragDisable: true,
                hasHover: true,
                data: { Url, ClickedCount, TrackLinkId },
            }));
    }, [showOnlyUnsubscribeLinks, trackedLinks]);

    const getSelectedRowsList = (rows) => {
        setMultiSelectedRows(rows);

        const trackLinkIds = rows.map((row) => row.data?.TrackLinkId);

        setSelectedTrackedLinkIds(trackLinkIds);

        if (!isEmpty(rows)) {
            sendRequest(getTrackLinkReportByIds.request, trackLinkIds);
        } else {
            setPlayers([]);
        }
    };

    const init = () => {
        const requestBody = {
            PromotionId: promotionId,
            CampaignType: campaignType,
            TemplateId: templateId,
        };
        doPostRequest(getTrackedLinks.request, {
            requestBody,
            successCallback: (data) => {
                setTrackedLinks(data);
            },
        }).then(() => {
            setIsLinksLoading(false);
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div className={classNames(styles.container, className)}>
            <div className={classNames(styles.wrapper)}>
                <BusyLoader isBusy={isNil(trackedLinksData)} type="spinner" spinnerSize="medium">
                    {!isNil(trackedLinksData) && (
                        <DataTable
                            name={t(linksTableTitle)}
                            classNames={playersDataTableStyles.table}
                            rowKey="TrackLinkId"
                            columnKey="dataKey"
                            tableKey={'TrackLinks'}
                            withPagination={false}
                            withPageSelector={false}
                            isVisibleOnlyDataTable={false}
                            data={trackedLinksData}
                            dataCount={trackedLinksData.length}
                            columns={columns}
                            multiSelectColumnConfig={multiSelectColumnConfig}
                            isLoading={isLinksLoading}
                            isHasRowMultiSelect={true}
                            rowActionBar={null}
                            getSelectedRowsList={getSelectedRowsList}
                            defaultMultiSelectedRows={trackedLinksData}
                            TitleActions={
                                <ButtonWithLoader
                                    isLoading={isDownloadLinksLoading}
                                    color="primary"
                                    appearance="minimal"
                                    onClick={downloadTrackedLinks}
                                    defaultClassNames="download-all"
                                    isDisabled={isEmpty(multiSelectedRows)}
                                >
                                    {t(l.DownloadAll)}
                                </ButtonWithLoader>
                            }
                        />
                    )}
                </BusyLoader>
            </div>

            <Divider type="vertical" withSpace={true} />

            <div className={styles.wrapper}>
                <PlayersDataTable
                    data={{ Data: players }}
                    config={PlayersColumns}
                    isLoading={isPlayersLoading}
                    TitleActions={
                        <div className={playersDataTableStyles.downloadPlayersToolbar}>
                            <DownloadToolbar
                                downloadClickHandler={downloadPlayersData}
                                withCountDropdown={true}
                                withTypeSelector={false}
                                isFileLoading={isDownloadPlayersLoading}
                                isDisableDownload={isEmpty(players)}
                            />
                            <Tooltip text={t(l.TooltipDownloadTypeInfo)} position="top">
                                <Icon type="bc-icon-info" />
                            </Tooltip>
                        </div>
                    }
                />
            </div>
        </div>
    );
};

PromotionTrackedLinksContent.displayName = 'PromotionTrackedLinksContent';

PromotionTrackedLinksContent.propTypes = {
    promotionId: PropTypes.string.isRequired,
    templateId: PropTypes.number,
    campaignType: PropTypes.string.isRequired,
    showOnlyUnsubscribeLinks: PropTypes.bool,
    templateName: PropTypes.string,
    linksTableTitle: PropTypes.string,
    setSelectedTrackedLinkIds: PropTypes.func,
    className: PropTypes.string,
};
PromotionTrackedLinksContent.defaultProps = {
    showOnlyUnsubscribeLinks: false,
    setSelectedTrackedLinkIds: noop,
    linksTableTitle: l.TrackedLink,
    className: '',
};

export default memo(PromotionTrackedLinksContent);
